import axios from 'axios';
import { BASE_API } from '../../axios';
import { TokenStorage } from '../../utils/cookieStorage';
import { sessionManager } from '../../utils/sessionManager';

// Separate axios instance for /api/documents (no /api/V1 prefix)
const docsAxios = axios.create({
  baseURL: BASE_API,// + '/api',
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

docsAxios.interceptors.request.use((config) => {
  const token = TokenStorage.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.method !== 'get') {
    const csrfToken = sessionStorage.getItem('csrf_token');
    if (csrfToken) {
      config.headers['x-csrf-token'] = csrfToken;
    }
  }
  return config;
});

docsAxios.interceptors.response.use(
  (response) => {
    sessionManager.extendSession();
    return response;
  },
  (error) => Promise.reject(error)
);

export const fetchDocuments = (params = {}) => {
  return docsAxios.get('/documents', { params });
};

export const createDocument = ({ title, pacsOrdId = undefined, templateId = undefined, type = undefined }) => {
  return docsAxios.post('/documents', { title, pacsOrdId, templateId, type });
};

export const fetchTemplates = (params) => {
  return docsAxios.post('/template-list-oodocs', params || {});
};

export const getEditorConfig = (docId, mode = 'edit', { fitToWidth = true } = {}) => {
  return docsAxios.get(`/documents/config/${docId}`, { params: { mode, ...(fitToWidth ? { fitToWidth: 'true' } : {}) } });
};

export const downloadDocument = async (docId, filename) => {
  const response = await docsAxios.get(`/documents/download/${docId}`, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(response.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'document.docx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadDocumentBlob = async (docId) => {
  const response = await docsAxios.get(`/documents/download/${docId}`, {
    responseType: 'blob',
  });
  return response.data;
};

export const updateDocumentStatus = (docId, status) => {
  return docsAxios.patch(`/documents/${docId}/status`, { status });
};

export const deleteDocument = (docId) => {
  return docsAxios.delete(`/documents/${docId}`);
};

export const copyDocument = ({ sourceDocumentId, pacsOrdId, title }) => {
  return docsAxios.post('/documents/copy', { sourceDocumentId, pacsOrdId, title });
};

export const getTemplateDownloadUrl = async (templateId) => {
  const res = await docsAxios.get(`/template-url-oodocs/${templateId}`);
  return res.data?.data?.fileUrl || null;
};

export const applyTemplateToDocument = (docId, templateId, action = 'replace') => {
  return docsAxios.post(`/documents/${docId}/apply-template`, { templateId, action });
};

// Load an earlier report of the same patient into the current document on the same terms
// as a template: body only, this study's header/footer kept, and the prior's signature
// block stripped server-side. Same response shape as applyTemplateToDocument.
export const applyPriorReportToDocument = (docId, sourceDocumentId, action = 'replace') => {
  return docsAxios.post(`/documents/${docId}/apply-prior-report`, { sourceDocumentId, action });
};

export const forceSaveDocument = (docId) => {
  return docsAxios.post(`/documents/${docId}/forcesave`);
};

// Stamp the selected proxy / co-signing doctor signatures into the report .docx while
// still editing (live preview before signoff). Backend rebuilds the whole signature
// block from what's sent, flushes current edits to disk, rewrites, and returns a fresh
// doc_key to reopen with. Returns 400 on a finalized report.
// Pass amend_file_url (from useInSessionCapture) so the backend stamps what is ON SCREEN.
// Unlike previewReportPdf this REWRITES the working file and rotates its doc_key, which makes
// the editor reopen and discards the live session — so content the backend does not receive
// here is destroyed, not merely stale. Responds 503 with code PREVIEW_RETRY when the latest
// edits could not be captured, in which case the document and its session are left untouched.
export const previewSignature = (docId, { proxy_user = null, co_signing_doctor = null, amend_file_url = null } = {}) => {
  return docsAxios.post(`/documents/${docId}/signature-preview`, { proxy_user, co_signing_doctor, amend_file_url });
};

// Re-reporting a signed study: fork the immutable signed snapshot into a throwaway scratch
// document to edit. Returns the new doc_id. Needed because the signed .docx must never be
// written to, while editing (and previewing signatures, which stamps the file in place)
// requires a writable file. The scratch has no report row, so it never shows up in Report
// History and is discarded if the amendment is abandoned.
export const createScratchCopy = (docId) => {
  return docsAxios.post(`/documents/${docId}/scratch-copy`);
};

// Drop a scratch document when the amendment is abandoned. Best-effort — the backend sweeps
// anything this misses — so callers should not surface failures.
export const discardScratchCopy = (docId) => {
  return docsAxios.post(`/documents/${docId}/discard-scratch`);
};

export const downloadDocumentPdf = async (docId) => {
  const response = await docsAxios.get(`/documents/${docId}/pdf`, {
    responseType: 'blob',
  });
  return response.data;
};

// Dry run of sign-off: the PDF the user WOULD get, rendered through the sign-off pipeline
// (in-session capture -> snapshot -> header/signature/QR stamp -> ConvertService) against a
// throwaway copy. Nothing is committed and the document being edited is left untouched —
// unlike previewSignature, this never rewrites the working file or rotates its doc_key.
// Pass amend_file_url (from useInSessionCapture) so the preview reflects what is on screen
// rather than the file on disk. Responds 503 with code PREVIEW_RETRY when the latest edits
// could not be captured.
export const previewReportPdf = async (docId, { proxy_user = null, co_signing_doctor = null, amend_file_url = null } = {}) => {
  const response = await docsAxios.post(
    `/documents/${docId}/pdf-preview`,
    { proxy_user, co_signing_doctor, amend_file_url },
    { responseType: 'blob' }
  );
  return response.data;
};

// ─── Enhanced Paste OnlyOffice plugin ──────────────────────────────────────
//
// The plugin runs in an iframe the Document Server creates, so it asks the host
// page to make this call on its behalf — see useOoPluginBridge. Routing it
// through docsAxios means the access token, CSRF token and base URL are handled
// exactly once, here.

// Clipboard RTF (or an HTML flavor) -> a formatted fragment the plugin injects
// at the cursor with PasteHtml. The old PACS puts only RTF on the clipboard and
// the Document Server discards it, which is why this round trip exists at all.
// Generous timeout: the backend hands the RTF to the ConvertService and waits.
export const convertRichPaste = ({ rtf = undefined, html = undefined, docId = undefined } = {}) => {
  return docsAxios.post('/documents/rtf-to-html', { rtf, html, docId }, { timeout: 120000 });
};
